import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { useAuth } from './AuthContext';

/**
 * PermissionsContext — fetches and caches effective RBAC permissions for the
 * currently-authenticated user. Components can call `usePermissions()` to gate
 * UI elements.
 *
 * Data shape (from GET /api/role-permissions/me/permissions):
 *   {
 *     legacy_admin: bool,
 *     permissions: { "<resource>": { view, create, edit, delete } },
 *     role_ids: [..],
 *   }
 */
const PermissionsContext = createContext(null);

export const PermissionsProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [permissions, setPermissions] = useState({});
  const [legacyAdmin, setLegacyAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const impersonating = !!(user?.impersonation?.active);

  const fetchPermissions = useCallback(async () => {
    if (!isAuthenticated) {
      setPermissions({});
      setLegacyAdmin(false);
      setLoading(false);
      setLoaded(true);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/role-permissions/me/permissions`, { withCredentials: true });
      setPermissions(res.data?.permissions || {});
      // Impersonation: never keep full-admin unlock even if API lags on identity
      setLegacyAdmin(!!res.data?.legacy_admin && !res.data?.impersonating && !user?.impersonation?.active);
    } catch {
      // On failure, fall back to legacy admin-only (deny-by-default for non-admins)
      setPermissions({});
      setLegacyAdmin(user?.role === 'admin' && !user?.impersonation?.active);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [isAuthenticated, user?.role, user?.id, user?.role_ids, user?.impersonation?.active]);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  // Effective flag — belt-and-suspenders vs stale state during user switch
  const isLegacyAdmin = legacyAdmin && !impersonating;

  const hasPermission = useCallback((resource, action = 'view') => {
    if (isLegacyAdmin) return true;
    const acts = permissions?.[resource];
    return !!(acts && acts[action]);
  }, [permissions, isLegacyAdmin]);

  const hasAnyAction = useCallback((resource) => {
    if (isLegacyAdmin) return true;
    const acts = permissions?.[resource];
    if (!acts) return false;
    return !!(acts.view || acts.create || acts.edit || acts.delete);
  }, [permissions, isLegacyAdmin]);

  const value = useMemo(() => ({
    permissions,
    legacyAdmin: isLegacyAdmin,
    loading,
    loaded,
    hasPermission,
    hasAnyAction,
    refresh: fetchPermissions,
  }), [permissions, isLegacyAdmin, loading, loaded, hasPermission, hasAnyAction, fetchPermissions]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Permissive fallback for pages outside the provider tree (rare)
    return {
      permissions: {},
      legacyAdmin: false,
      loading: false,
      loaded: true,
      hasPermission: () => false,
      hasAnyAction: () => false,
      refresh: () => {},
    };
  }
  return ctx;
};
